// UploadControl.tsx
import React, { useState } from 'react';
import { Control } from '../ControlRenderer';
import { Button, Typography } from '@mui/material';
import { FormModeType, useFormData } from '@/components/FormDataProvider';
import ModalBox, { Action } from '../ModalBox';
import { useConfig } from '@/components/ConfigProvider';
import { get, set } from '../utils/Utils';
import { useAuth } from '@/components/AuthProvider';
import useActionHandler from '@/hooks/useActionHandler';

export interface UploadControl extends Control {
  uploadAction: Action;
}

interface UploadControlProps {
  control: Control;
  data: any;
  mode: FormModeType;
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

const UploadControl: React.FC<UploadControlProps> = ({ control, data, mode, onComplete, onError, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const { apiUrl } = useConfig(); // Get the API root URL from config
  const { token } = useAuth(); // Get the bearer token
  const { formKey, formData, refreshForm, setFormDataElement } = useFormData();
  const { executeAction, UIAction, handleSuccess, handleFailure, handleClose } = useActionHandler({ onComplete: onComplete, onError: onError, onClose: onClose });

  //console.log(JSON.stringify(data))

  const action = (control as UploadControl).uploadAction ?? {};
  const accept = action.payload && action.payload.accept ? action.payload.accept : ".pdf,.doc,.docx,.jpg,.jpeg,.png";
  const uploadData = control.dataKey ? get(data, control.dataKey) : data;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    setSelectedFile(file || null);
  };

  const handleUpload = async () => {

    if (!selectedFile) {
      console.error('No file selected');
      return;
    }

    setUploading(true);

    try {
      if (action.payload && action.payload.endpoint) {
        const postData = new FormData();
        postData.append('file', selectedFile);
        postData.append('formKey', get(data, "FormKey", formKey.toString()));

        const response = await fetch(`${apiUrl}/${action.payload.endpoint}/upload`, {
          method: 'POST',
          body: postData,
          headers: {
            'Authorization': `Bearer ${token}`
            // Add any additional headers if needed
          }
        });

        if (!response.ok) {
          throw new Error('Failed to upload file');
        }

        // if the target is an array add a new element otherwise copy the returned props
        var ret = await response.json();
        if (Array.isArray(uploadData)) {
          uploadData.unshift(ret);
          refreshForm();
        } else {
          for (const prop in ret) {
            if (action.payload.setRoot) {
              set(formData, prop, ret[prop]);
            }
            else if (action.payload.resultDataKey) {
              if (action.payload.noRefresh) {
                set(formData, `${action.payload.resultDataKey}_${prop}`, ret[prop]);
              }
              else {
                setFormDataElement(`${action.payload.resultDataKey}_${prop}`, ret[prop]);
              }
            } else {
              set(data, prop, ret[prop]);
            }
          }
        }

        // File uploaded successfully
        console.log('File uploaded successfully');
        setSelectedFile(null);
        if (action.successAction) {
          executeAction(action.successAction, data);
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      if (action.failureAction) {
        executeAction(action.failureAction, data);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
      <input
        type="file"
        id="file-input"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={accept}
      />
      <label htmlFor="file-input">
        <Button variant="contained" component="span">
          Select File
        </Button>
      </label>
      <Typography variant="body1" gutterBottom style={{ margin: '0px 10px' }}>
        {selectedFile ? `Selected File: ${selectedFile.name}` : 'No file selected'}
      </Typography>
      {selectedFile && (
        <Button variant="contained" onClick={handleUpload} disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      )}
      {UIAction &&
        <ModalBox
          action={UIAction}
          data={data}
          onDataChange={() => { }}
          mode={mode}
          onComplete={() => handleSuccess(UIAction, data)}
          onError={() => handleFailure(UIAction, data)}
          onClose={() => handleClose(UIAction, data)} />}
    </div>
  );
};

export default UploadControl;